<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { EFileType, getFileType } from "@/composables/shared/useFile";
import {
  type TUploadFieldSharedProps,
  useUploadFieldBase,
} from "@/composables/ui/inputs/useUploadFieldBase";

import AppImg from "@/components/shared/files/AppImg.vue";
import AvatarImageEditor from "@/components/ui/inputs/AvatarImageEditor.vue";
import BaseDialog from "@/components/shared/dialogs/BaseDialog.vue";
import BaseDialogActions from "@/components/shared/dialogs/BaseDialogActions.vue";
import UploadFieldBody from "@/components/ui/inputs/UploadFieldBody.vue";
import UploadFieldImageOverlayActions from "@/components/ui/inputs/uploadField/UploadFieldImageOverlayActions.vue";
import type { TFile } from "@/api/shared/file";
import { useSnackStore } from "@/stores/snackStore";

const props = withDefaults(defineProps<TUploadFieldSharedProps>(), {
  multiple: false,
  enableImageEditor: false,
});

const emit = defineEmits<{
  editing: [boolean];
}>();

const { t } = useI18n();
const snackStore = useSnackStore();

const {
  acceptAttribute,
  uploadedFiles,
  localFiles,
  fileInput,
  isBusy,
  isDragOver,
  isHovered,
  initFromFieldValue,
  uploadMultipleFiles,
  onRemove,
  openFileBrowser,
  isFileDisabled,
  getUploadProgress,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onMouseEnter,
  onMouseLeave,
} = useUploadFieldBase(props);

const showImageEditor = ref(false);
const editorOriginalFile = ref<File | null>(null);
const previewDialogVisible = ref(false);

watch(showImageEditor, (val) => {
  emit("editing", val);
});

watch(previewDialogVisible, (val) => {
  if (!val) {
    onEditorCancel();
  }
});

const singleUploadedFile = computed(() => uploadedFiles.value[0] ?? null);
const singleLocalFile = computed(() => localFiles.value[0] ?? null);

const singleUploadState = computed(() => {
  if (!singleUploadedFile.value) return null;
  return getUploadProgress(singleUploadedFile.value);
});

const isSingleUploading = computed(() => !!singleUploadState.value);

const isSingleImage = computed(() => {
  if (!singleUploadedFile.value) return false;
  return getFileType(singleUploadedFile.value) === EFileType.IMAGE;
});

const isSingleFileDisabled = computed(() => {
  if (!singleUploadedFile.value) return false;
  return isFileDisabled(singleUploadedFile.value);
});

async function handlePickedFiles(selectedFiles: File[]) {
  if (!selectedFiles[0] || isBusy.value) return;

  await uploadMultipleFiles(selectedFiles);

  if (fileInput.value) {
    fileInput.value.value = "";
  }
}

async function onNativeInputChange(event: Event) {
  const target = event.target as HTMLInputElement | null;
  const files = Array.from(target?.files ?? []);
  await handlePickedFiles(files);
}

async function onDrop(event: DragEvent) {
  event.preventDefault();
  isDragOver.value = false;

  if (isBusy.value) return;

  const files = Array.from(event.dataTransfer?.files ?? []);
  await handlePickedFiles(files);
}

onMounted(async () => {
  await initFromFieldValue();
});

function handleEditorError(error: unknown) {
  console.error("[UploadFieldSingle]", error);

  if (error instanceof Error && error.message) {
    snackStore.showError(error.message, false);
    return;
  }

  snackStore.showError(t("generic.messages.failure.unknown"), false);
}

async function openImageEditor() {
  if (props.multiple) {
    console.warn("[UploadField] Image editor is only supported in single-file mode.");
    return;
  }

  const currentFile = singleUploadedFile.value;
  if (!currentFile || !isSingleImage.value || isSingleFileDisabled.value) {
    return;
  }

  try {
    const localFile = singleLocalFile.value;

    if (localFile && localFile.size > 0) {
      editorOriginalFile.value = localFile;
    } else {
      editorOriginalFile.value = await hydrateRemoteFile(currentFile);
    }

    showImageEditor.value = true;
  } catch (error) {
    handleEditorError(error);
  }
}

async function onEditorSave(payload: { file: File }) {
  try {
    await uploadMultipleFiles([payload.file]);
    previewDialogVisible.value = false;
  } catch (error) {
    handleEditorError(error);
  } finally {
    onEditorCancel();
  }
}

function onEditorCancel() {
  showImageEditor.value = false;
  editorOriginalFile.value = null;
}

function closePreviewDialog() {
  previewDialogVisible.value = false;
}

function removeCurrentFile() {
  if (!singleUploadedFile.value) return;

  onRemove(singleUploadedFile.value);
  closePreviewDialog();
}

async function hydrateRemoteFile(file: TFile): Promise<File> {
  const response = await fetch(file.file, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch file "${file.name}" for editing.`);
  }

  const blob = await response.blob();

  return new File([blob], file.original_name || file.name, {
    type: blob.type || file.mimetype || "application/octet-stream",
    lastModified: new Date(file.created_at as string | number | Date).getTime(),
  });
}
</script>

<template>
  <div
    class="tw:flex tw:h-full tw:w-full tw:min-w-0 tw:flex-col tw:bg-surface"
    :class="props.variant === 'avatar' ? 'tw:rounded-2xl' : 'tw:overflow-hidden tw:rounded-lg'"
  >
    <input
      ref="fileInput"
      type="file"
      class="tw:hidden"
      :accept="acceptAttribute"
      :disabled="isBusy"
      :multiple="props.multiple"
      @change="onNativeInputChange"
    />

    <AvatarImageEditor
      v-if="showImageEditor && props.editorInline"
      :file="editorOriginalFile"
      :aspect-ratio="props.editorAspectRatio"
      :max-display-width="props.editorMaxWidth"
      :max-display-height="props.editorMaxHeight"
      class="tw:w-full"
      @save="onEditorSave"
      @cancel="onEditorCancel"
    />

    <div
      v-else-if="!singleUploadedFile"
      role="button"
      :tabindex="isBusy ? -1 : 0"
      :aria-disabled="isBusy"
      class="upload-dropzone tw:relative tw:flex tw:h-full tw:w-full tw:min-w-0 tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:overflow-hidden tw:border tw:border-dashed tw:border-on-surface/20 tw:bg-transparent tw:p-4 tw:text-center"
      :class="{
        'upload-dropzone--drag': isDragOver,
        'tw:cursor-not-allowed tw:opacity-60': isBusy,
        'tw:cursor-pointer': !isBusy,
        'upload-dropzone--hover': isHovered,
        'upload-dropzone--avatar tw:rounded-2xl': props.variant === 'avatar',
        'tw:rounded-lg': props.variant !== 'avatar',
      }"
      @click="openFileBrowser"
      @keydown.enter.prevent="openFileBrowser"
      @keydown.space.prevent="openFileBrowser"
      @dragover="onDragOver"
      @dragenter="onDragEnter"
      @dragleave="onDragLeave"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
      @drop="onDrop"
    >
      <upload-field-body
        :is-hovered="isHovered"
        :icon="props.icon"
        :img-src="props.imgSrc"
        :theme-aware-icon="props.themeAwareIcon"
        :label="label"
        :optional="optional"
        :variant="props.variant"
      />
    </div>

    <div
      v-else
      class="upload-field-filled tw:flex tw:min-h-0 tw:w-full tw:min-w-0 tw:flex-1 tw:flex-col"
      :class="props.variant === 'avatar' ? 'tw:justify-center' : ''"
    >
      <div
        v-if="isSingleUploading"
        class="upload-field-progress tw:flex tw:h-full tw:items-center tw:justify-center tw:overflow-hidden tw:border tw:border-on-surface/10 tw:bg-surface-muted"
        :class="
          props.variant === 'avatar'
            ? 'tw:h-full tw:w-full tw:min-w-0 tw:flex-1 tw:min-h-[96px] tw:sm:min-h-[120px] tw:rounded-2xl'
            : 'tw:min-h-[120px] tw:sm:min-h-[180px] tw:w-full tw:min-w-0 tw:flex-1 tw:rounded-lg'
        "
      >
        <loading
          :label="t('generic.file.error.uploadInProgress')"
          :progress="singleUploadState?.progress ?? 0"
          :size="60"
        />
      </div>

      <template v-else>
        <div
          class="upload-field-preview tw:group tw:relative tw:flex tw:w-full tw:min-w-0 tw:flex-1 tw:items-center tw:justify-center tw:overflow-hidden tw:border tw:border-on-surface/10 tw:bg-surface-muted"
          :class="[
            props.variant === 'avatar'
              ? 'tw:h-full tw:w-full tw:min-w-0 tw:flex-1 tw:min-h-[96px] tw:sm:min-h-[120px] tw:rounded-2xl'
              : [
                  'tw:min-h-[120px] tw:sm:min-h-[180px] tw:w-full tw:min-w-0 tw:flex-1 tw:rounded-lg',
                  props.overlayActions ? 'tw:h-full tw:min-h-0' : '',
                ],
          ]"
        >
          <app-img
            v-if="isSingleImage"
            :src="singleUploadedFile.file"
            class="tw:h-full tw:w-full tw:object-contain"
            :class="props.overlayActions ? '' : 'tw:max-h-[220px] tw:sm:max-h-[320px]'"
          />

          <upload-field-image-overlay-actions
            v-if="props.overlayActions"
            :show-edit="props.enableImageEditor && isSingleImage"
            :disabled="isSingleFileDisabled || isBusy"
            @edit="openImageEditor"
            @remove="removeCurrentFile"
          />

          <template v-else-if="props.enableImageEditor && isSingleImage">
            <div
              class="upload-overlay-tint upload-overlay-reveal tw:pointer-events-none tw:absolute tw:inset-0 tw:opacity-0 tw:transition-opacity tw:duration-200 tw:group-hover:opacity-100"
              :class="props.variant === 'avatar' ? 'tw:rounded-2xl' : 'tw:rounded-lg'"
            />
            <div
              class="upload-overlay-reveal tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:opacity-0 tw:transition-opacity tw:duration-200 tw:group-hover:opacity-100"
            >
              <v-btn
                icon="mdi-pencil"
                size="small"
                variant="flat"
                color="secondary"
                class="tw:!shadow-md"
                :aria-label="t('generic.action.edit')"
                :disabled="isSingleFileDisabled || isBusy"
                @click="previewDialogVisible = true"
              />
            </div>
          </template>
        </div>
      </template>
    </div>

    <BaseDialog
      v-if="props.enableImageEditor && !props.overlayActions"
      v-model="previewDialogVisible"
      max-width="480"
      icon="mdi-image-outline"
    >
      <template #title>
        {{ t("generic.action.editEntity", { entity: props.label || t("generic.fields.picture") }) }}
      </template>

      <template #body>
        <AvatarImageEditor
          v-if="showImageEditor"
          :file="editorOriginalFile"
          :aspect-ratio="props.editorAspectRatio"
          :max-display-width="380"
          :max-display-height="380"
          @save="onEditorSave"
          @cancel="onEditorCancel"
        />

        <div
          v-else
          class="tw:group tw:relative tw:flex tw:h-[260px] tw:sm:h-[380px] tw:w-full tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-lg tw:bg-surface-muted"
        >
          <app-img
            v-if="isSingleImage && singleUploadedFile"
            :src="singleUploadedFile.file"
            class="tw:h-full tw:w-full tw:object-contain"
          />

          <upload-field-image-overlay-actions
            show-edit
            :disabled="isSingleFileDisabled || isBusy"
            @edit="openImageEditor"
            @remove="removeCurrentFile"
          />
        </div>
      </template>

      <template
        v-if="!showImageEditor"
        #actions
      >
        <BaseDialogActions
          :primary="{
            icon: 'mdi-close',
            text: t('generic.action.close'),
            action: closePreviewDialog,
          }"
        />
      </template>
    </BaseDialog>
  </div>
</template>

<style src="@/assets/styles/components/uploadDropzone.scss" scoped lang="scss" />

<style scoped>
/* `group-hover` never fires on a touchscreen, so the edit overlay stays invisible
   and effectively unreachable on mobile. */
@media (hover: none) {
  .upload-overlay-reveal {
    opacity: 1 !important;
  }
}

.upload-overlay-tint {
  background: rgba(var(--v-theme-secondary), 0.05);
}
</style>
